import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, Warning, CircleNotch } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// RP06 (D10) — page publique d'activation : la personne invitée choisit son mot de
// passe (lien à usage unique reçu par e-mail), puis elle est connectée.
function messageErreur(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (err?.response?.status === 429) return 'Trop de tentatives. Réessayez dans quelques minutes.';
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  if (typeof detail === 'string') return detail;
  return fallback;
}

export default function Activation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [etat, setEtat] = useState('chargement'); // chargement | formulaire | invalide
  const [invitation, setInvitation] = useState(null);
  const [erreurLien, setErreurLien] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    let annule = false;
    axios.get(`${API_URL}/api/auth/invitation/${encodeURIComponent(token || '')}`)
      .then(({ data }) => { if (!annule) { setInvitation(data); setEtat('formulaire'); } })
      .catch((err) => {
        if (annule) return;
        setErreurLien(messageErreur(err, "Ce lien n'est pas valide. Demandez au cabinet de vous renvoyer l'invitation."));
        setEtat('invalide');
      });
    return () => { annule = true; };
  }, [token]);

  const minLength = invitation?.min_length || 8;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < minLength) return setError(`Le mot de passe doit contenir au moins ${minLength} caractères.`);
    if (password !== confirm) return setError('Les deux mots de passe ne correspondent pas.');
    setEnvoi(true);
    try {
      await axios.post(`${API_URL}/api/auth/activate`, { token, password }, { withCredentials: true });
      await checkAuth();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code && code !== 'mot_de_passe') {
        setErreurLien(messageErreur(err, "Ce lien n'est plus valide."));
        setEtat('invalide');
      } else {
        setError(messageErreur(err, 'Activation impossible. Réessayez.'));
      }
    } finally {
      setEnvoi(false);
    }
  };

  if (etat === 'chargement') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] p-4" data-testid="activation-loading">
        <CircleNotch size={32} className="animate-spin text-[#C75B39]" />
      </div>
    );
  }

  if (etat === 'invalide') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] p-4" data-testid="activation-invalid">
        <div className="max-w-md w-full p-8 bg-white border border-slate-200 text-center">
          <Warning size={40} className="mx-auto text-red-500 mb-3" weight="fill" />
          <h2 className="text-xl font-bold mb-2">Lien d'invitation inutilisable</h2>
          <p className="text-slate-600 mb-6">{erreurLien}</p>
          <Link to="/login" className="inline-block bg-[#C75B39] text-white px-6 py-3 font-semibold hover:bg-[#A94B2E]">
            Aller à la connexion
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] p-4" data-testid="activation-form">
      <div className="max-w-md w-full p-6 sm:p-8 bg-white border border-slate-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#C75B39] text-white flex items-center justify-center">
            <Lock size={20} weight="bold" />
          </div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Activer mon accès
          </h2>
        </div>
        <p className="text-slate-600 mb-1">Bonjour {invitation?.name},</p>
        <p className="text-slate-600 mb-6">
          Choisissez le mot de passe de votre compte <strong>{invitation?.email}</strong>.
          Vous vous connecterez ensuite avec cette adresse et ce mot de passe.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="activation-password">Mot de passe</label>
            <input
              id="activation-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 border border-slate-300 focus:outline-none focus:border-[#C75B39]"
              placeholder={`${minLength} caractères minimum`}
              required
              data-testid="activation-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="activation-confirm">Confirmer le mot de passe</label>
            <input
              id="activation-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full p-3 border border-slate-300 focus:outline-none focus:border-[#C75B39]"
              required
              data-testid="activation-confirm"
            />
          </div>
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200">
              <Warning size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          <button
            type="submit"
            disabled={envoi}
            className="w-full bg-[#C75B39] text-white py-3 font-semibold hover:bg-[#A94B2E] disabled:opacity-60"
            data-testid="activation-submit"
          >
            {envoi ? 'Activation…' : 'Choisir mon mot de passe et me connecter'}
          </button>
        </form>
        <p className="mt-6 text-center text-xs text-slate-500">
          Ce lien est personnel et ne peut servir qu'une fois.
        </p>
      </div>
    </div>
  );
}
